import React, { useMemo, useState } from "react";
import { supabase } from "../../src/lib/supabaseClient";

const INDUSTRY_OPTIONS = [
  "Legal Services",
  "Healthcare / Medical",
  "Financial Services",
  "Human Resources",
  "Insurance",
  "Consulting / Advisory",
  "Technology / SaaS",
  "Education",
  "Government / Public Sector",
  "Other",
];

const ROLE_OPTIONS = [
  "Advisor",
  "Consultant",
  "Attorney / Legal Professional",
  "Founder / Owner",
  "Executive",
  "Sales Professional",
  "Compliance Professional",
  "Other",
];

const initialForm = {
  full_name: "",
  email: "",
  linkedin_profile: "",
  phone_whatsapp: "",
  primary_industry: INDUSTRY_OPTIONS[0],
  role_title: ROLE_OPTIONS[0],
  org_types_30_days: "",
  network_description: "",
};

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

export default function Partners() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const functionsBase = useMemo(() => {
    return (
      process.env.REACT_APP_SUPABASE_FUNCTIONS_BASE ||
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1`
    );
  }, []);

  function handleChange(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    if (!form.full_name.trim()) {
      setErrorMessage("Please enter the applicant's full name.");
      return;
    }

    if (!isValidEmail(form.email)) {
      setErrorMessage("Please enter the applicant's email address.");
      return;
    }

    if (!form.primary_industry.trim()) {
      setErrorMessage("Please select a primary industry.");
      return;
    }

    if (!form.role_title.trim()) {
      setErrorMessage("Please select a current role.");
      return;
    }

    if (!form.org_types_30_days.trim()) {
      setErrorMessage("Please list 3 types of organizations they could introduce within 30 days.");
      return;
    }

    if (!form.network_description.trim()) {
      setErrorMessage("Please describe the applicant's typical clients or network.");
      return;
    }

    setSubmitting(true);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;

      const response = await fetch(`${functionsBase}/partner_application`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: process.env.REACT_APP_SUPABASE_ANON_KEY || "",
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({
          ...form,
          source_path: "/partners",
        }),
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload?.error || "Application submission failed.");
      }

      if (payload?.warning) {
        setSuccessMessage(
          "Your application was submitted successfully. Email delivery is pending, but your application is in the review queue."
        );
      } else {
        setSuccessMessage(
          "Your application was submitted successfully. We will review it and follow up if there is a fit."
        );
      }

      setForm(initialForm);
    } catch (error) {
      setErrorMessage(error?.message || "Edge Function returned a non-2xx status code");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="mx-auto max-w-7xl px-6 py-10 md:px-8">
        <div className="mb-8 rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-slate-900 via-blue-900 to-sky-700 px-6 py-10 text-white md:px-10">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-sky-200">
              Advisory Sales Partner Program
            </p>
            <h1 className="max-w-4xl text-3xl font-semibold tracking-tight md:text-5xl">
              Introduce AI Fine Protector™ to the right decision-makers and build recurring income.
            </h1>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-100 md:text-lg">
              AI Fine Protector™ helps organizations understand their potential financial exposure
              to AI regulation across multiple jurisdictions—before regulators take action.
            </p>
          </div>

          <div className="grid gap-6 px-6 py-8 md:grid-cols-3 md:px-10">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <h2 className="mb-3 text-lg font-semibold">Compensation</h2>
              <p className="text-slate-700 leading-8">
                25% of the initial payment, plus 12.5% monthly recurring commission for up to 12
                months.
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <h2 className="mb-3 text-lg font-semibold">Best Fit</h2>
              <p className="text-slate-700 leading-8">
                Consultants, advisors, legal-adjacent professionals, and operators with
                decision-maker access.
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <h2 className="mb-3 text-lg font-semibold">Model</h2>
              <p className="text-slate-700 leading-8">
                Network-led introductions, optional demo support, fast conversion potential, and
                manual onboarding.
              </p>
            </div>
          </div>
        </div>

        <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="space-y-6">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-2xl font-semibold tracking-tight">Why this opportunity exists</h2>
              <div className="mt-4 space-y-4 text-slate-700 leading-8">
                <p>
                  AI adoption is accelerating while enforcement risk is becoming more visible. Many
                  organizations still cannot answer a simple question:
                  <span className="font-medium text-slate-900">
                    {" "}
                    “What is our financial exposure from AI regulation?”
                  </span>
                </p>
                <p>
                  AI Fine Protector™ gives them a structured, decision-ready answer without
                  requiring technical integration or a long implementation cycle.
                </p>
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-2xl font-semibold tracking-tight">Who this is for</h2>
              <ul className="mt-4 space-y-4 text-slate-700 leading-8">
                <li>• Consultants and advisors serving regulated industries</li>
                <li>• Legal-adjacent professionals with trusted networks</li>
                <li>• Industry operators who can reach executives quickly</li>
                <li>• Professionals who want recurring revenue, not one-off referrals</li>
              </ul>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
            <h2 className="text-3xl font-semibold tracking-tight">Apply to Become a Partner</h2>
            <p className="mt-3 text-slate-600 leading-7">
              Complete the application below. We will review it manually and reach out if there is
              a fit.
            </p>

            <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="full_name">
                  Full Name
                </label>
                <input
                  id="full_name"
                  name="full_name"
                  type="text"
                  value={form.full_name}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none ring-0 transition focus:border-sky-500"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="email">
                  Email <span className="text-slate-500">(their email address)</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none ring-0 transition focus:border-sky-500"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="linkedin_profile">
                  LinkedIn Profile <span className="text-slate-500">(optional)</span>
                </label>
                <input
                  id="linkedin_profile"
                  name="linkedin_profile"
                  type="text"
                  value={form.linkedin_profile}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none ring-0 transition focus:border-sky-500"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="phone_whatsapp">
                  Phone / WhatsApp <span className="text-slate-500">(optional)</span>
                </label>
                <input
                  id="phone_whatsapp"
                  name="phone_whatsapp"
                  type="text"
                  value={form.phone_whatsapp}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none ring-0 transition focus:border-sky-500"
                />
              </div>

              <div className="grid gap-5 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="primary_industry">
                    Primary Industry
                  </label>
                  <select
                    id="primary_industry"
                    name="primary_industry"
                    value={form.primary_industry}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-sky-500"
                  >
                    {INDUSTRY_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="role_title">
                    Current Role
                  </label>
                  <select
                    id="role_title"
                    name="role_title"
                    value={form.role_title}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-sky-500"
                  >
                    {ROLE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="org_types_30_days">
                  List 3 types of organizations you could introduce this to within 30 days
                </label>
                <textarea
                  id="org_types_30_days"
                  name="org_types_30_days"
                  rows={4}
                  value={form.org_types_30_days}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-sky-500"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="network_description">
                  Describe your typical clients or network
                </label>
                <textarea
                  id="network_description"
                  name="network_description"
                  rows={5}
                  value={form.network_description}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-sky-500"
                />
              </div>

              {errorMessage ? (
                <div className="rounded-2xl border border-rose-300 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">
                  {errorMessage}
                </div>
              ) : null}

              {successMessage ? (
                <div className="rounded-2xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
                  {successMessage}
                </div>
              ) : null}

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex w-full items-center justify-center rounded-2xl bg-sky-500 px-5 py-4 text-base font-semibold text-white transition hover:bg-sky-600 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {submitting ? "Submitting..." : "Submit Application"}
              </button>

              <p className="text-sm leading-7 text-slate-500">
                Applications are reviewed manually. Approved partners will receive onboarding materials and access instructions.
              </p>
            </form>
          </div>
        </div>

        <div className="mt-10 border-t border-slate-200 pt-6 text-center text-sm text-slate-500">
          © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
        </div>
      </div>
    </div>
  );
}
